// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useState } from 'react';

import type { ItemData } from '@aps_sdk/data-management';
import type { AutodeskCloudModel, AutodeskUrn, ProjectId, Spool } from '@evolvemep/api-ts';

import { isNil } from 'helpers/isNotNil';

import { useAutodeskModels } from './useAutodeskModels';
import { useSpoolAutodeskCloudModel } from './useSpoolAutodeskCloudModel';

export type SelectedModelState = {
  evolveModel: AutodeskCloudModel;
  autodeskModel: ItemData;
  urn: string;
};

type Opts = {
  /**
   * When provided, the model this spool was published from is selected by default,
   * instead of the project's first model.
   */
  spool?: Spool['spool'] | null;
  hideInaccessible?: boolean;
};

/**
 * Loads the Autodesk models available on a project and tracks which one is selected.
 *
 * Spread the returned `modelSelectProps` (`models` / `modelCache` / `loading` / `value` / `onChange`) into `ModelSelect`,
 * and read `selectedModel` for the resolved selection.
 */
export const useModelSelection = (projectId: ProjectId, { spool = null, hideInaccessible = false }: Opts = {}) => {
  const { models, modelCache, loading } = useAutodeskModels(projectId, { hideInaccessible });
  const { urn: preferredUrn, resolved } = useSpoolAutodeskCloudModel(projectId, spool);
  const [selectedUrn, setSelectedUrn] = useState<AutodeskUrn | null>(null);

  // The model list lags a project change by a render, so anything from the previous project is ignored.
  const projectModels = useMemo(
    () => models?.filter(({ projectId: modelProjectId }) => modelProjectId === projectId),
    [models, projectId],
  );

  // The value to use inside the `ModelSelect`. If `selectedUrn` is `null` and we're not waiting to fetch a preferred URN, falls back to a default.
  const value = useMemo<AutodeskUrn | null>(() => {
    if (projectModels?.some(({ urn }) => urn === selectedUrn)) return selectedUrn;
    if (!resolved) return null;
    // Prefer the model from the spool if available, otherwise fall back to the first project model if available, then null.
    return (
      projectModels?.find(({ urn }) => isNil(preferredUrn) || urn === preferredUrn)?.urn ??
      projectModels?.[0]?.urn ??
      null
    );
  }, [projectModels, selectedUrn, resolved, preferredUrn]);

  // Augments the selected model with its Autodesk tip version.
  const selectedModel = useMemo<SelectedModelState | null>(() => {
    if (isNil(value)) return null;
    const evolveModel = projectModels?.find(({ urn }) => urn === value);
    const autodeskModel = modelCache[value];
    const tipData = autodeskModel?.relationships.tip.data;
    if (isNil(evolveModel) || isNil(autodeskModel) || tipData?.type !== 'versions' || isNil(tipData.id)) return null;
    return { evolveModel, autodeskModel, urn: tipData.id };
  }, [value, projectModels, modelCache]);

  return {
    selectedModel,
    modelSelectProps: {
      models: projectModels,
      /** Autodesk metadata for each model, keyed by URN. A `null` entry means the user has no access to it. */
      modelCache,
      loading,
      value,
      onChange: setSelectedUrn,
    },
  };
};
